import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ChevronDown, ChevronRight, Menu, RotateCcw, Info } from 'react-feather';
import { reorderChartOfAccountDepartments, replaceCoaRevenueBasisMappings } from '../utils/api';
import { InlineMultiSelect, InlineSelect, InlineInput, StatusDot } from '../components/InlineTable';
import { ConfigToolbar, ToolbarButton, ConfigEmptyState } from '../components/ConfigPages';
import { USALI_OPTIONS, usaliLabel, acctLabel, deptRowKey, groupLinks } from './coa/coaShared';

/**
 * P&L Mapping tab (Chart of Accounts page) — configures how the P&L report
 * reads the chart:
 *
 *  - Revenue basis (% Rev / departmental pairing): each expense department is
 *    tied to the revenue account(s) it is measured against; individual
 *    accounts can override their department. With no mapping the report falls
 *    back to stripped department-name matching ("Food Exp" ↔ "Food Rev"),
 *    then location total revenue.
 *  - USALI section: which band the department occupies on the Dept/Summary/
 *    GOP views (Operated → departmental profit, Undistributed → subtracts to
 *    GOP, Management fee → Income Before Fixed Charges, Fixed charges →
 *    EBITDA, Below EBITDA → Net Income). Auto infers from the department's
 *    activity.
 *  - Department name / code: edited inline (PATCH chartOfAccountsDepartments).
 *  - Presentation order: drag rows; per-location order overrides the
 *    All-locations order and can be reset.
 *
 * Data (accounts, departments incl. locationDisplayOrder, links, the grouped
 * deptRows) is owned by ChartOfAccountsPage and passed down; this tab only
 * saves and reports back through onLinksChange / onPatchDepartment /
 * refetchDepartments.
 */

const linkedText = (n) => `${n} linked account${n === 1 ? '' : 's'}`;

export default function CoaRevenueMappingTab({
  organizationId,
  locationId = null,
  locationName = null,
  accounts,
  departments,
  deptRows,
  links,
  linksLoading = false,
  onLinksChange,
  onPatchDepartment,
  refetchDepartments,
}) {
  const [expanded, setExpanded] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all'); // all | unmapped (inferred) | mapped (section set)
  // deptId → usaliSection saved this session (optimistic until the page merges the PATCH response).
  const [sectionOverrides, setSectionOverrides] = useState(() => new Map());
  // deptId → presentationName saved this session, same optimistic pattern.
  const [presentationOverrides, setPresentationOverrides] = useState(() => new Map());
  // deptId → displayOrder saved this session; seeds from the entity rows.
  // Ordered departments lead the P&L / forecast presentation; unordered ones
  // keep the fallback order after them.
  const [orderOverrides, setOrderOverrides] = useState(() => new Map());
  // Drag-to-reorder state (native HTML5 DnD, same pattern as ColumnPicker).
  const [dragKey, setDragKey] = useState(null);
  const [dragOver, setDragOver] = useState(null); // { key, after }
  const [resetting, setResetting] = useState(false);

  useEffect(() => { setOrderOverrides(new Map()); }, [organizationId, locationId]);

  // A location with ANY override rows uses ITS OWN order.
  const hasLocationOrder = useMemo(
    () => Boolean(locationId) && (departments || []).some((d) => d.locationDisplayOrder != null),
    [locationId, departments]
  );

  const revenueOptions = useMemo(
    () => (accounts || [])
      .filter((a) => a.accountType === 'Revenue' && !a.isDeleted)
      .sort((a, b) => String(a.accountNumber || '').localeCompare(String(b.accountNumber || ''), undefined, { numeric: true }))
      .map((a) => ({ value: a.id, label: acctLabel(a) })),
    [accounts]
  );

  const { byAcct: linksByAcct } = useMemo(() => groupLinks(links), [links]);

  /** Selected values for a source: [{value, label}] so targets configured at
   *  another location still render with their number/name. */
  const valueFor = (rows) => (rows || []).map((l) => ({
    value: l.revenueChartOfAccountId,
    label: `${l.revenueAccountNumber ? `${l.revenueAccountNumber} · ` : ''}${l.revenueAccountName || l.revenueChartOfAccountId}`,
  }));

  const saveSource = useCallback(async (source, nextIds, label) => {
    const prev = links;
    try {
      const saved = await replaceCoaRevenueBasisMappings(organizationId, source, nextIds);
      // The endpoint returns the source's rows; merge them over the old ones.
      onLinksChange((cur) => [
        ...cur.filter((l) => (source.chartOfAccountDepartmentId != null
          ? l.chartOfAccountDepartmentId !== source.chartOfAccountDepartmentId
          : l.chartOfAccountId !== source.chartOfAccountId)),
        ...(Array.isArray(saved) ? saved : []),
      ]);
    } catch (err) {
      console.error('Error saving revenue mapping:', err);
      toast.error(`Failed to save the revenue basis for ${label}. Try again.`);
      onLinksChange(prev);
    }
  }, [links, organizationId, onLinksChange]);

  const saveSection = useCallback(async (dept, next) => {
    const prev = sectionOverrides;
    setSectionOverrides((cur) => { const m = new Map(cur); m.set(dept.entity.id, next || null); return m; });
    try {
      await onPatchDepartment(dept.entity.id, { usaliSection: next || null });
    } catch (err) {
      console.error('Error saving USALI section:', err);
      toast.error(`Failed to save the USALI section for ${dept.name}. Try again.`);
      setSectionOverrides(prev);
    }
  }, [sectionOverrides, onPatchDepartment]);

  // The statement caption is deliberately NOT saveDeptField('name'): renaming a
  // department cascades to ChartOfAccount.department, while presentationName
  // only changes how the P&L prints. Departments sharing a caption collapse
  // onto one statement line.
  const savePresentationName = useCallback(async (dept, value) => {
    const clean = String(value ?? '').trim();
    const prev = presentationOverrides;
    setPresentationOverrides((cur) => { const m = new Map(cur); m.set(dept.entity.id, clean || null); return m; });
    try {
      await onPatchDepartment(dept.entity.id, { presentationName: clean || null });
    } catch (err) {
      console.error('Error saving statement line:', err);
      toast.error(`Failed to save the statement line for ${dept.name}. Try again.`);
      setPresentationOverrides(prev);
    }
  }, [presentationOverrides, onPatchDepartment]);

  const saveDeptField = useCallback(async (dept, field, value) => {
    const clean = String(value ?? '').trim();
    if (field === 'name' && !clean) {
      toast.error('Department name cannot be empty.');
      throw new Error('empty name');
    }
    try {
      await onPatchDepartment(dept.entity.id, { [field]: field === 'code' ? (clean || null) : clean });
      toast.success(field === 'name' ? `Renamed to "${clean}".` : `Code saved for ${dept.name}.`);
    } catch (err) {
      console.error(`Error saving department ${field}:`, err);
      toast.error(`Failed to save the ${field} for ${dept.name}. Try again.`);
      throw err;
    }
  }, [onPatchDepartment]);

  const presentationFor = useCallback((dept) => {
    if (!dept.entity) return null;
    if (presentationOverrides.has(dept.entity.id)) return presentationOverrides.get(dept.entity.id);
    return dept.entity.presentationName || null;
  }, [presentationOverrides]);

  const sectionFor = useCallback((dept) => {
    if (!dept.entity) return null;
    if (sectionOverrides.has(dept.entity.id)) return sectionOverrides.get(dept.entity.id);
    return dept.section;
  }, [sectionOverrides]);

  // Drag-set departments lead in their saved order; unordered ones (and
  // name-only rows, which have nothing to persist an order on) follow
  // alphabetically — mirroring how the P&L / forecast views present them.
  const orderedRows = useMemo(() => {
    const orderOf = (dept) => {
      if (!dept.entity) return null;
      const saved = hasLocationOrder ? dept.entity.locationDisplayOrder : dept.entity.displayOrder;
      const o = orderOverrides.has(dept.entity.id) ? orderOverrides.get(dept.entity.id) : saved;
      return o === null || o === undefined ? null : Number(o);
    };
    return [...(deptRows || [])].sort((a, b) => {
      const oa = orderOf(a); const ob = orderOf(b);
      if ((oa !== null) !== (ob !== null)) return oa !== null ? -1 : 1;
      if (oa !== null && oa !== ob) return oa - ob;
      return a.name.localeCompare(b.name);
    });
  }, [deptRows, hasLocationOrder, orderOverrides]);

  const statusCounts = useMemo(() => {
    let mapped = 0;
    orderedRows.forEach((d) => { if (sectionFor(d)) mapped += 1; });
    return { all: orderedRows.length, unmapped: orderedRows.length - mapped, mapped };
  }, [orderedRows, sectionFor]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orderedRows.filter((d) => {
      if (status === 'mapped' && !sectionFor(d)) return false;
      if (status === 'unmapped' && sectionFor(d)) return false;
      if (!q) return true;
      return d.name.toLowerCase().includes(q) || String(d.entity?.code || '').toLowerCase().includes(q);
    });
  }, [orderedRows, search, status, sectionFor]);
  const filtering = Boolean(search.trim()) || status !== 'all';

  /** Persist the dragged sequence: every entity-backed row gets 1..N in its
   *  current visual position; name-only rows can't carry an order and stay in
   *  the fallback tail. */
  const saveOrder = useCallback(async (rows) => {
    const orderedIds = rows.filter((d) => d.entity).map((d) => d.entity.id);
    if (orderedIds.length === 0) return;
    const prev = orderOverrides;
    setOrderOverrides(() => new Map(orderedIds.map((id, i) => [id, i + 1])));
    try {
      await reorderChartOfAccountDepartments(organizationId, orderedIds, locationId);
      await refetchDepartments();
      toast.success(locationId ? `Order saved for ${locationName || 'this location'}.` : 'All-locations order saved.');
    } catch (err) {
      console.error('Error saving department order:', err);
      toast.error('Failed to save the department order. Try again.');
      setOrderOverrides(prev);
    }
  }, [orderOverrides, organizationId, locationId, locationName, refetchDepartments]);

  /** Clear this location's overrides so it inherits the All-locations order. */
  const resetToOrgOrder = useCallback(async () => {
    if (!locationId) return;
    setResetting(true);
    try {
      await reorderChartOfAccountDepartments(organizationId, [], locationId);
      setOrderOverrides(new Map());
      await refetchDepartments();
      toast.success('Reset to the All-locations order.');
    } catch (err) {
      console.error('Error resetting department order:', err);
      toast.error('Failed to reset the department order. Try again.');
    } finally {
      setResetting(false);
    }
  }, [organizationId, locationId, refetchDepartments]);

  const handleDrop = useCallback((targetKey) => {
    if (!dragKey || dragKey === targetKey) return;
    const keys = orderedRows.map(deptRowKey);
    const next = keys.filter((k) => k !== dragKey);
    const idx = next.indexOf(targetKey) + (dragOver?.after ? 1 : 0);
    next.splice(idx, 0, dragKey);
    const byKey = new Map(orderedRows.map((d) => [deptRowKey(d), d]));
    saveOrder(next.map((k) => byKey.get(k)).filter(Boolean));
  }, [dragKey, dragOver, orderedRows, saveOrder]);

  const toggleExpand = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const orderingText = !locationId
    ? 'Ordering: All locations — applies to every property (each shows only its own departments).'
    : hasLocationOrder
      ? `Ordering: custom order for ${locationName || 'this location'}.`
      : `Ordering: ${locationName || 'this location'} inherits the All-locations order — drag to make it unique.`;

  return (
    <div className="it-frame">
      <ConfigToolbar
        search={{ value: search, onChange: setSearch, placeholder: 'Search department or code…' }}
        status={{ value: status, counts: statusCounts, onChange: setStatus, labels: { all: 'All', unmapped: 'Inferred', mapped: 'Section set' } }}
        meta={`${orderedRows.length.toLocaleString()} department${orderedRows.length === 1 ? '' : 's'}`}
        actions={locationId && hasLocationOrder ? (
          <ToolbarButton variant="ghost" icon={RotateCcw} onClick={resetToOrgOrder} disabled={resetting}>
            {resetting ? 'Resetting…' : 'Reset to All-locations order'}
          </ToolbarButton>
        ) : null}
      />

      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--lodge-onyx-100)' }}>
        <div className="cp-note">
          <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong>{orderingText}</strong>
            {' '}Tie each expense department to the revenue it is measured against (% Rev and departmental pairing) and pick the
            USALI section that decides where it lands on the Dept / Summary / GOP views — Operated reports departmental profit,
            Undistributed subtracts to GOP, then Management fee (→ Income Before Fixed Charges), Fixed charges (→ EBITDA) and
            Below EBITDA (interest, depreciation, taxes → Net Income) in that order. Auto infers from the chart: departments pair by
            name, revenue-bearing departments read as Operated, the rest Undistributed unless named non-operating. Drag rows to set
            the presentation order within each band on the P&L, Summary statement and forecast; departments you haven't ordered
            follow in GL account-number order.{filtering ? ' Dragging is disabled while a filter is active.' : ''}
          </div>
        </div>
      </div>

      <div className="it-scroll">
        <table className="it-tbl" style={{ minWidth: 1180 }}>
          <thead>
            <tr>
              <th style={{ width: 34 }}></th>
              <th style={{ minWidth: 300 }}>Department / Account</th>
              <th style={{ minWidth: 170 }}>USALI section</th>
              <th style={{ minWidth: 180 }}>Statement line</th>
              <th style={{ minWidth: 220 }}>Currently</th>
              <th style={{ minWidth: 320 }}>Measured against revenue</th>
              <th style={{ width: 60, textAlign: 'center' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((dept) => {
              const key = deptRowKey(dept);
              const isOpen = expanded.has(key);
              const deptLinks = dept.links;
              const section = sectionFor(dept);
              const inferredLabel = usaliLabel(dept.inferred);
              const currently = deptLinks.length > 0
                ? linkedText(deptLinks.length)
                : dept.stemMatch
                  ? `Auto · pairs with “${dept.stemMatch}”`
                  : 'Auto · % of location total revenue';
              const isDragSource = dragKey === key;
              const dropHere = dragOver?.key === key && dragKey && dragKey !== key;
              const canDrag = Boolean(dept.entity) && !filtering;
              const statusTitle = !dept.entity
                ? 'No department record — create it on the Accounts tab to set a section or order'
                : section
                  ? `USALI section set: ${usaliLabel(section)}${deptLinks.length ? ` · ${linkedText(deptLinks.length)}` : ''}`
                  : `USALI section inferred as ${inferredLabel} — set it explicitly to pin it`;
              return (
                <React.Fragment key={key}>
                  <tr
                    className="cp-row"
                    style={{
                      background: 'var(--lodge-onyx-50)',
                      opacity: isDragSource ? 0.45 : 1,
                      boxShadow: dropHere
                        ? (dragOver.after ? 'inset 0 -2px 0 var(--lodge-orange-400)' : 'inset 0 2px 0 var(--lodge-orange-400)')
                        : undefined,
                    }}
                    draggable={canDrag}
                    onDragStart={canDrag ? (e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      try { e.dataTransfer.setData('text/plain', key); } catch (_) { /* IE */ }
                      setDragKey(key);
                    } : undefined}
                    onDragOver={(e) => {
                      if (!dragKey || dragKey === key) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      const rect = e.currentTarget.getBoundingClientRect();
                      const after = e.clientY > rect.top + rect.height / 2;
                      setDragOver((cur) => (cur?.key === key && cur.after === after ? cur : { key, after }));
                    }}
                    onDrop={(e) => { e.preventDefault(); handleDrop(key); setDragKey(null); setDragOver(null); }}
                    onDragEnd={() => { setDragKey(null); setDragOver(null); }}
                  >
                    <td style={{ width: 34, textAlign: 'center' }}>
                      {dept.expenseAccounts.length > 0 && (
                        <button
                          type="button"
                          onClick={() => toggleExpand(key)}
                          title={isOpen ? 'Hide accounts' : 'Show accounts for per-account overrides'}
                          aria-expanded={isOpen}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--lodge-onyx-500)', display: 'inline-flex' }}
                        >
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingLeft: 8 }}>
                        <Menu
                          size={12}
                          style={{ color: canDrag ? 'var(--lodge-onyx-300)' : 'var(--lodge-onyx-100)', cursor: canDrag ? 'grab' : 'default', flexShrink: 0 }}
                          title={dept.entity
                            ? (filtering ? 'Clear the search / filter to drag' : 'Drag to set the presentation order on the P&L and forecast')
                            : 'No department record — create it on the Accounts tab to set an order'}
                        />
                        {dept.entity ? (
                          <>
                            <div style={{ flex: 1, minWidth: 160 }}>
                              <InlineInput
                                value={dept.entity.name || ''}
                                onChange={(v) => saveDeptField(dept, 'name', v)}
                                placeholder="Department name…"
                                rowActive
                                ariaLabel={`Department name for ${dept.name}`}
                              />
                            </div>
                            <div style={{ width: 96, flexShrink: 0 }}>
                              <InlineInput
                                value={dept.entity.code || ''}
                                onChange={(v) => saveDeptField(dept, 'code', v)}
                                placeholder="Code"
                                rowActive
                                mono
                                ariaLabel={`Department code for ${dept.name}`}
                              />
                            </div>
                          </>
                        ) : (
                          <div className="cp-cell" style={{ paddingLeft: 8 }}>
                            <div className="cp-name">{dept.name}</div>
                            <div className="cp-code">No department record</div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {dept.entity ? (
                        <InlineSelect
                          value={section}
                          options={USALI_OPTIONS}
                          onChange={(next) => saveSection(dept, next)}
                          placeholder={`Auto · ${inferredLabel}`}
                          rowActive
                          minWidth={180}
                          ariaLabel={`USALI section for ${dept.name}`}
                        />
                      ) : (
                        <span className="cp-cell" style={{ fontSize: 12, color: 'var(--lodge-onyx-400)' }}>Auto · {inferredLabel}</span>
                      )}
                    </td>
                    <td>
                      {dept.entity ? (
                        <InlineInput
                          value={presentationFor(dept) || ''}
                          onChange={(v) => savePresentationName(dept, v)}
                          placeholder={dept.entity.name || 'Same as department'}
                          rowActive
                          ariaLabel={`Statement line for ${dept.name}`}
                        />
                      ) : (
                        <span className="cp-cell" style={{ fontSize: 12, color: 'var(--lodge-onyx-400)' }}>—</span>
                      )}
                    </td>
                    <td className="cp-cell" style={{ fontSize: 12, color: deptLinks.length ? 'var(--lodge-onyx-700)' : 'var(--lodge-onyx-400)' }}>
                      {dept.expenseAccounts.length > 0 || deptLinks.length ? currently : '—'}
                    </td>
                    <td>
                      {dept.expenseAccounts.length === 0 && !deptLinks.length ? (
                        <span className="cp-cell" style={{ fontSize: 12, color: 'var(--lodge-onyx-400)' }}>No expense accounts</span>
                      ) : dept.entity ? (
                        <InlineMultiSelect
                          value={valueFor(deptLinks)}
                          options={revenueOptions}
                          onChange={(next) => saveSource({ chartOfAccountDepartmentId: dept.entity.id }, next, dept.name)}
                          placeholder="Auto (name match / total revenue)…"
                          rowActive
                          minWidth={320}
                          ariaLabel={`Revenue basis for ${dept.name}`}
                        />
                      ) : (
                        <span className="cp-cell" style={{ fontSize: 12, color: 'var(--lodge-onyx-400)' }} title="This department string has no chartOfAccountDepartments entry — create it on the Accounts tab first.">
                          No department record — name matching only
                        </span>
                      )}
                    </td>
                    <td style={{ width: 60, textAlign: 'center' }}>
                      <StatusDot state={!dept.entity ? 'none' : section ? 'complete' : 'partial'} title={statusTitle} />
                    </td>
                  </tr>
                  {isOpen && [...dept.expenseAccounts]
                    .sort((a, b) => String(a.accountNumber || '').localeCompare(String(b.accountNumber || ''), undefined, { numeric: true }))
                    .map((acct) => {
                      const acctLinks = linksByAcct.get(acct.id) || [];
                      return (
                        <tr key={acct.id} className="cp-row">
                          <td></td>
                          <td className="cp-cell" style={{ fontSize: 12.5, color: 'var(--lodge-onyx-700)', paddingLeft: 34 }}>{acctLabel(acct)}</td>
                          <td></td>
                          <td className="cp-cell" style={{ fontSize: 12, color: acctLinks.length ? 'var(--lodge-onyx-700)' : 'var(--lodge-onyx-400)' }}>
                            {acctLinks.length ? linkedText(acctLinks.length) : 'Inherits department'}
                          </td>
                          <td>
                            <InlineMultiSelect
                              value={valueFor(acctLinks)}
                              options={revenueOptions}
                              onChange={(next) => saveSource({ chartOfAccountId: acct.id }, next, acctLabel(acct))}
                              placeholder="Inherit department…"
                              rowActive
                              minWidth={320}
                              ariaLabel={`Revenue basis for ${acctLabel(acct)}`}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <StatusDot state={acctLinks.length ? 'complete' : 'none'} title={acctLinks.length ? `Overrides department: ${linkedText(acctLinks.length)}` : 'Inherits the department revenue basis'} />
                          </td>
                        </tr>
                      );
                    })}
                </React.Fragment>
              );
            })}
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={7}>
                  {linksLoading ? (
                    <div style={{ padding: 36, textAlign: 'center', fontSize: 13, color: 'var(--lodge-onyx-400)' }}>Loading P&L mappings…</div>
                  ) : orderedRows.length === 0 ? (
                    <ConfigEmptyState
                      title="No departments to map"
                      body="Assign departments to your revenue and expense accounts on the Accounts tab — they show up here with their USALI section and revenue basis."
                    />
                  ) : (
                    <ConfigEmptyState
                      title="No departments match"
                      body="Try a different search or status filter."
                      action={<ToolbarButton variant="ghost" onClick={() => { setSearch(''); setStatus('all'); }}>Clear filters</ToolbarButton>}
                    />
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
